import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/** A module a learner can self-enrol on (or already has). */
export interface TrainingCatalogEntry {
  module_id: string;
  title: string;
  description: string | null;
  already_assigned: boolean;
}

const KEY = ['training-catalog'] as const;

export function useTrainingCatalog(enabled = true) {
  return useQuery({
    queryKey: KEY,
    enabled,
    queryFn: async () =>
      (await api<{ catalog: TrainingCatalogEntry[] }>('/api/me/training/catalog')).catalog,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: KEY });
  qc.invalidateQueries({ queryKey: ['me-view'] });
}

/** Self-enrol on an optional module from the catalogue. */
export function useEnrolTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moduleId: string) =>
      api<{ enrolled: boolean }>(`/api/me/training/catalog/${moduleId}/enrol`, { method: 'POST' }),
    onSuccess: () => invalidate(qc),
  });
}

/** Drop a self-enrolled optional module. */
export function useUnenrolTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moduleId: string) =>
      api<{ removed: boolean }>(`/api/me/training/catalog/${moduleId}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(qc),
  });
}
